import { api } from "@shared/lib/api";
import {
  ACCOUNT_STATUS,
  BackednResponse,
  IBrand,
  IBrands,
  IBrandUser,
  IBrandUserEditRequest,
  ICreateBrandUser,
  IEditBrandUser,
  IGetOrganisations,
  IOrganisationBrandUser,
  IOrganisationFacets,
  IPagination,
} from "@shared/lib/types";
import {
  IBrandBilling,
  IOrgansationBilling,
} from "@shared/lib/types/brandBilling";
import {
  IAdRightsRequest,
  IBrandOrganisation,
} from "@shared/lib/types/brandOrganisation";
import { PermissionGroup } from "@shared/lib/types/permissions";
import { BILLING_TYPE } from "../constants";
import {
  ICreateBrandRequest,
  ICreateOrganisationRequest,
  IEditFinanceSpocsRequest,
} from "../types";

export class OrganisationService {
  public static async getOrganisations(
    limit: number,
    pageNo: number,
    searchQuery?: string,
    status?: ACCOUNT_STATUS[],
    type?: string[],
    permissionedUserIds?: string[],
    companyName?: string[],
    industries?: string[]
  ) {
    const payload: {
      [key: string]: any;
    } = {};

    if (searchQuery) {
      payload["searchQuery"] = searchQuery;
    }
    if (limit) {
      payload["limit"] = limit;
    }
    if (pageNo) {
      payload["pageNo"] = pageNo;
    }

    if (permissionedUserIds && permissionedUserIds?.length) {
      payload["permissionedUserIds"] = permissionedUserIds;
    }

    if (status && status?.length) {
      payload["status"] = status;
    }
    if (type && type?.length) {
      payload["type"] = type;
    }
    if (companyName && companyName?.length) {
      payload["companyName"] = companyName;
    }

    if (industries) {
      payload["industries"] = industries;
    }

    const url = `brand-organisation/search/`;
    const organisations = (await api
      .post(url, {
        json: payload,
      })
      .json()) as BackednResponse<IGetOrganisations>;

    return organisations;
  }

  public static async getOrganisationById(id: string) {
    const url = `brand-organisation/${id}`;
    const brand = (await api
      .get(url)
      .json()) as BackednResponse<IBrandOrganisation>;
    return brand;
  }
  public static async getOrganisationFacets() {
    return (await api
      .get("brand-organisation/facets")
      .json()) as BackednResponse<IOrganisationFacets>;
  }

  public static async addUserToOrganisation(
    userId: string,
    brandOrganisationId: string,
    brandIds: string[]
  ) {
    await api.patch("brand-organisation/access", {
      json: {
        userId,
        brandOrganisationId,
        brandIds,
      },
    });
  }

  public static async deleteUserFromOrganisation(
    userId: string,
    brandOrganisationId: string
  ) {
    await api.delete("brand-organisation/access", {
      json: {
        userId,
        brandOrganisationId,
      },
    });
  }

  public static async createOrganisation(
    organisation: ICreateOrganisationRequest
  ) {
    await api.post("brand-organisation", {
      json: {
        ...organisation,
      },
    });
  }

  public static async editOrganisation(
    organisation: Partial<ICreateOrganisationRequest>
  ) {
    await api.put("brand-organisation", {
      json: {
        ...organisation,
        brandOrganisationId: organisation?.brandOrganisationId,
      },
    });
  }

  public static async createOrganisationUser(
    brandOrganisationId: string,
    data: ICreateBrandUser
  ) {
    const perms = data?.permissionGroupIds;

    await api.post("user/brand", {
      json: {
        ...data,
        brandOrganisationId,
        permissionGroupIds: perms,
        emailNotificationActive: true,
      },
    });
  }
  public static async updateOrganisationUser(
    brandUserId: string,
    data: Partial<IEditBrandUser>
    // brandOrganisationId: string,
  ) {
    await api.patch("user/brand", {
      json: {
        ...data,
        // brandOrganisationId,
        brandUserId,
        emailNotificationActive: true,
      },
    });
  }

  public static async createBrand(brand: ICreateBrandRequest) {
    await api.post("brand", {
      json: {
        ...brand,
        brandOrganisationId: brand.brandOrganisationId,
      },
    });
  }

  public static async updateBrand(brand: Partial<IBrand>, brandId: string) {
    return (await api
      .patch(`brand`, {
        json: {
          ...brand,
          brandId,
        },
      })
      .json()) as BackednResponse<IBrand>;
  }

  public static async searchPermissionGroups(
    brandOrganisationId: string,
    limit?: number,
    pageNo?: number
  ) {
    return (await api
      .post("brand-organisation/permission-groups/search", {
        json: {
          filterBy: { brandOrganisationId },
          limit,
          pageNo,
        },
      })
      .json()) as BackednResponse<{
      data: PermissionGroup[];
      pagination: IPagination;
    }>;
  }

  public static async getBrandOrganisationBilling(brandOrganisationId: string) {
    const url = "brand-organisation/billing/search";
    const params = new URLSearchParams();
    if (brandOrganisationId) {
      params.set("brandOrganisationId", brandOrganisationId);
    }
    const query = params.toString();
    return (await api
      .get(`${url}${query ? `?${query}` : ""}`)
      .json()) as BackednResponse<IOrgansationBilling>;
  }

  public static async editSingleModeBilling(payload: {
    brandOrganisationId: string;
    brandBillingDetails: IBrandBilling;
  }) {
    const url = "brand-organisation/billing/edit";
    return await api.put(url, {
      json: {
        ...payload,
      },
    });
  }

  public static async createInitialBilling(payload: {
    brandOrganisationId: string;
    billingMode: BILLING_TYPE;
    brandBillingDetails: IBrandBilling[];
  }) {
    const url = "brand-organisation/billing/enable";
    return await api.post(url, {
      json: {
        ...payload,
      },
    });
  }

  public static async getBrandBillingById(brandId: string) {
    const params = new URLSearchParams();
    if (brandId) {
      params.set("brandId", brandId);
    }
    const query = params.toString();
    const url = `billing/brand/search/by-brand-id${query ? `?${query}` : ""}`;
    return (await api.get(url).json()) as BackednResponse<IBrandBilling>;
  }

  public static async searchBrandUser(payload: {
    searchQuery?: string;
    brandOrganisationId?: string;
    accountStatuses?: string[];
    pageNo?: number;
    limit?: number;
    brandIds?: string[];
  }) {
    const url = `user/brand/search`;
    const response = (await api
      .post(url, {
        json: payload,
      })
      .json()) as BackednResponse<{
      data: IOrganisationBrandUser[];
      pagination: IPagination;
    }>;
    return response;
  }

  public static async editBrandUser(payload: IBrandUserEditRequest) {
    const url = `user/brand`;
    const { brandOrganisationId, ...rest } = payload;
    const response = (await api
      .patch(url, {
        json: { ...rest },
      })
      .json()) as BackednResponse<IBrandUser>;
    return response;
  }

  public static async editNotificationRecipients(payload: {
    brandOrganisationId: string;
    notificationReciepents: {
      userId: string;
      isAllBrands: boolean;
      brandIds: string[];
    };
  }) {
    return (await api
      .post(`brand-organisation/settings/ad-rights/notification-reciepents`, {
        json: { ...payload },
      })
      .json()) as BackednResponse<IBrandOrganisation>;
  }

  public static async deleteNotificationRecipient(payload: {
    brandOrganisationId: string;
    notificationReciepentsId: string;
  }) {
    return (await api
      .delete(`brand-organisation/settings/ad-rights/notification-reciepents`, {
        json: { ...payload },
      })
      .json()) as BackednResponse<IBrandOrganisation>;
  }
  public static async getOrganisationBrands(brandOrganisationId: string) {
    const params = new URLSearchParams();
    if (brandOrganisationId) {
      params.set("brandOrganisationIds", brandOrganisationId);
    }
    params.set("limit", "1000");
    const query = params.toString();

    return (await api
      .get(`brand${query ? `?${query}` : ""}`)
      .json()) as BackednResponse<IBrands>;
  }
  public static async editAdRightSettings(payload: IAdRightsRequest) {
    return (await api
      .post(`brand-organisation/settings/ad-rights`, {
        json: { ...payload },
      })
      .json()) as BackednResponse<IBrandOrganisation>;
  }

  public static async editFinanceSpocs(payload: IEditFinanceSpocsRequest) {
    return await api.patch("brand-organisation/billing/edit-finance-pocs", {
      json: { ...payload },
    });
  }
}
